import { CommonModule } from '@angular/common';
import { Component, inject, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '@app/auth/auth.service';
import { FormModule } from '@app/shared/components/form/form.module';
import { PasswordConfirm, PatronValidators } from '@app/shared/components/form/validators';
import {
  AlertComponent,
  ButtonComponent,
  IconComponent,
  MenuItem,
  ModalComponent,
  OverlayComponent,
  StepsComponent,
} from '@teorema/patron';

@Component({
  selector: 'register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormModule,
    ButtonComponent,
    RouterModule,
    AlertComponent,
    StepsComponent,
    OverlayComponent,
    ModalComponent,
    IconComponent,
  ],
})
export class RegisterComponent {
  loading = false;
  loadingData = false;
  registerEmailForm: FormGroup;
  validateCodeForm: FormGroup;
  registerUserForm: FormGroup;
  registerCompanyForm: FormGroup;
  registerAddressForm: FormGroup;
  steps: MenuItem[] = [
    {
      label: 'Usuário',
    },
    {
      label: 'Empresa',
    },
    {
      label: 'Endereço',
    },
  ];

  activeStep:
    | 'register-email'
    | 'validate-code'
    | 'register-user'
    | 'register-company'
    | 'register-address' = 'register-email';
  message?: string;
  errorMessage?: string;
  successModalVisible = false;

  taxRegimes: any[] = [
    { label: 'Simples Nacional', value: 'SN' },
    { label: 'Lucro Presumido', value: 'LP' },
    { label: 'Lucro Real', value: 'LR' },
  ];

  ieDestInds: any[] = [
    {
      label: '(1) Contribuinte ICMS (informar a IE do destinatário)',
      value: '1',
    },
    {
      label: '(2) Contribuinte isento de Inscrição no cadastro de Contribuintes do ICMS',
      value: '2',
    },
    {
      label:
        '(9) Não Contribuinte, que pode ou não possuir Inscrição Estadual no Cadastro de Contribuintes do ICMS',
      value: '9',
    },
  ];

  private formBuilder = inject(FormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);

  constructor() {
    this.registerEmailForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.validateCodeForm = this.formBuilder.group({
      email: [{ value: '', disabled: true }],
      code: ['', [Validators.required]],
    });

    this.registerUserForm = this.formBuilder.group(
      {
        email: [{ value: '', disabled: true }],
        password: ['', [Validators.required, Validators.minLength(6)]],
        password_confirmation: ['', [Validators.required, Validators.minLength(6)]],
        name: ['', [Validators.required]],
      },
      {
        validators: [PasswordConfirm('password', 'password_confirmation')],
      }
    );

    this.registerCompanyForm = this.formBuilder.group({
      cnpj: ['', [Validators.required, PatronValidators.cnpj]],
      name: ['', Validators.required],
      fantasy_name: ['', Validators.required],
      phone: ['', Validators.required],
      ie: ['', Validators.required],
      ie_dest_ind: ['9', Validators.required],
      tax_regime: ['SN', Validators.required],
    });

    this.registerAddressForm = this.formBuilder.group({
      cep: ['', Validators.required],
      address: ['', Validators.required],
      number: ['', Validators.required],
      neighborhood: ['', Validators.required],
      city: [null, Validators.required],
      complement: [''],
    });
  }

  registerEmail() {
    this.errorMessage = undefined;
    this.message = undefined;
    const { email } = this.registerEmailForm.value;
    if (email) {
      this.loading = true;
      this.authService
        .registerRequest(email)
        .then((response: any) => {
          if (response?.message) {
            if (response?.status == 'ERROR') {
              this.errorMessage = response.message;
            } else {
              this.goToStep({
                step: 'validate-code',
                infoMessage: 'Digite o código enviado em seu email para continuar',
              });
              this.validateCodeForm.patchValue({
                email,
              });
            }
          }
        })
        .catch(error => {
          this.goToStep({ errorMessage: 'Houve um erro ao enviar o código no email' });
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  enableResendEmail() {
    this.goToStep({ step: 'register-email' });
  }

  validateCode() {
    this.errorMessage = undefined;
    this.message = undefined;
    const { email } = this.registerEmailForm.value;
    const { code } = this.validateCodeForm.value;

    if (email && code) {
      this.loading = true;
      this.authService
        .registerValidate(email, code)
        .then((response: any) => {
          if (response?.message) {
            this.goToStep({
              step: 'register-user',
              infoMessage: 'Código validado! Informe agora os dados de usuário:',
            });
            this.registerUserForm.patchValue({
              email,
            });
          }
        })
        .catch((error: any) => {
          this.goToStep({
            errorMessage: error.error.message ?? 'Houve um erro ao validar o código',
          });
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  registerUser() {
    this.goToStep({ step: 'register-company', infoMessage: 'Informe alguns dados da sua empresa' });
  }

  async changeCnpj() {
    const { cnpj } = this.registerCompanyForm.value;
    if (!cnpj) return;
    this.loadingData = true;
    await this.authService
      .getCnpjData(cnpj)
      .then(async (response: any) => {
        const { nome, fantasia, telefone } = response;

        if (nome) {
          this.registerCompanyForm.get('name')?.setValue(nome);
        }

        if (fantasia) {
          this.registerCompanyForm.get('fantasy_name')?.setValue(fantasia);
        }

        if (telefone) {
          this.registerCompanyForm.get('phone')?.setValue(telefone.replace(/\D/g, ''));
        }

        this.fillIe(response);
        this.fillAddress(response);

        // A busca por CNPJ não retorna o código IBGE do município (municipio vem só como
        // nome). Resolve a cidade (com IBGE) a partir do CEP retornado, igual ao customer-edit.
        await this.resolveCityByCep(response?.cep);
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        this.loadingData = false;
      });
  }

  async changeCep() {
    const { cep } = this.registerAddressForm.value;
    if (!cep) return;
    this.loadingData = true;
    await this.authService
      .getCepData(cep)
      .then(response => {
        this.fillAddress(response);
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => (this.loadingData = false));
  }

  fillIe(company: any) {
    const activeIe = (company?.inscricoes_estaduais ?? []).find((i: any) => i?.ativo);
    const ie = company?.inscricao_estadual_principal || activeIe?.inscricao_estadual;

    if (ie) {
      this.registerCompanyForm.get('ie')?.setValue(String(ie).replace(/\D/g, ''));
      this.registerCompanyForm.get('ie_dest_ind')?.setValue('1');
    } else {
      this.registerCompanyForm.get('ie')?.setValue('ISENTO');
      this.registerCompanyForm.get('ie_dest_ind')?.setValue('2');
    }
  }

  fillAddress(address: any) {
    const { cep, logradouro, numero, complemento, bairro, municipio, ibge } = address;
    if (cep) {
      this.registerAddressForm.get('cep')?.setValue(cep);
    }

    if (logradouro) {
      this.registerAddressForm.get('address')?.setValue(logradouro);
    }

    if (numero) {
      this.registerAddressForm.get('number')?.setValue(numero);
    }

    if (complemento) {
      this.registerAddressForm.get('complement')?.setValue(complemento);
    }

    if (bairro) {
      this.registerAddressForm.get('neighborhood')?.setValue(bairro);
    }

    // localidade vem das respostas de CEP; municipio das de CNPJ.
    this.setCity(address.city, address.ibge ?? ibge, address.localidade ?? municipio);
  }

  // Monta o objeto de município para o patron-select (mesmo padrão do person-form).
  // Só define quando há IBGE (o select depende dele); preserva o objeto completo do
  // CEP (com state.bacen) quando disponível.
  private setCity(cityData: any, ibgeFallback?: any, nameFallback?: any) {
    const cityIbge = cityData?.ibge ?? ibgeFallback;
    const cityName = cityData?.name ?? nameFallback;
    if (!cityIbge || !cityName) return;

    const _name = `${cityIbge} - ${cityName}`;
    this.registerAddressForm.get('city')?.setValue({
      _name,
      ibge: cityIbge,
      name: cityName,
      ...(cityData ?? {}),
    });
  }

  // Resolve a cidade a partir do CEP quando ela ainda não tem IBGE (caso do preenchimento
  // via CNPJ, que não retorna o código do município).
  private async resolveCityByCep(cep: any) {
    const cleanCep = String(cep ?? '').replace(/\D/g, '');
    if (!cleanCep) return;
    if (this.registerAddressForm.get('city')?.value?.ibge) return;

    await this.authService
      .getCepData(cleanCep)
      .then((response: any) => {
        this.setCity(response?.city, response?.ibge, response?.localidade);
      })
      .catch(error => {
        console.log(error);
      });
  }

  registerCompany() {
    this.goToStep({ step: 'register-address', infoMessage: 'Informe o endereço da empresa' });
  }

  async registerAddress() {
    const registerEmailFormValue = this.registerEmailForm.value;
    const registerUserFormValue = this.registerUserForm.value;
    const registerCompanyFormValue = this.registerCompanyForm.value;
    const registerAddressFormValue = this.registerAddressForm.value;

    this.loading = true;

    // O município agora é um objeto vindo do patron-select (modelName="cities"),
    // então o city_ibge sempre vem preenchido. O bacen não é retornado pela busca de
    // cidades, então reconsultamos o CEP como fallback quando ainda estiver faltando.
    const city = registerAddressFormValue.city;
    let cityIbge = city?.ibge ?? '';
    let countryBacen = city?.state?.country_bacen ?? city?.state?.bacen ?? '';

    if (!cityIbge || !countryBacen) {
      const cep = registerAddressFormValue.cep;
      if (cep) {
        await this.authService
          .getCepData(cep)
          .then((responseCep: any) => {
            if (!cityIbge && responseCep?.ibge) {
              cityIbge = responseCep.ibge;
            }
            const bacen = responseCep?.city?.state?.bacen;
            if (!countryBacen && bacen) {
              countryBacen = bacen;
            }
          })
          .catch(error => {
            console.log(error);
          });
      }
    }

    const body = {
      email: registerEmailFormValue.email,
      name: registerUserFormValue.name,
      password: registerUserFormValue.password,
      password_confirmation: registerUserFormValue.password_confirmation,
      company: {
        cnpj: registerCompanyFormValue.cnpj,
        name: registerCompanyFormValue.name,
        fantasy_name: registerCompanyFormValue.fantasy_name,
        ie: registerCompanyFormValue.ie,
        ie_dest_ind: registerCompanyFormValue.ie_dest_ind,
        tax_regime: registerCompanyFormValue.tax_regime,
        cnae: null,
        gender: 'M',
        type: 'J',
        address: {
          zip_code: registerAddressFormValue.cep,
          address: registerAddressFormValue.address,
          number: registerAddressFormValue.number,
          neighborhood: registerAddressFormValue.neighborhood,
          complement: registerAddressFormValue.complement,
          city_ibge: cityIbge,
          country_bacen: countryBacen,
          type: 'C',
        },
        contact_phone: {
          active: true,
          type: 'F',
          value: registerCompanyFormValue.phone,
        },
        contact_email: {
          active: true,
          type: 'E',
          value: registerEmailFormValue.email,
        },
      },
    };

    await this.authService
      .registerComplete(body)
      .then((response: any) => {
        if (response) {
          this.openSuccessModal();
        }
      })
      .catch(error => {
        this.goToStep({
          errorMessage: error.error.message ?? 'Houve um erro ao salvar o cadastro',
        });
      })
      .finally(() => (this.loading = false));
  }

  openSuccessModal() {
    this.successModalVisible = false;
    setTimeout(() => {
      this.successModalVisible = true;
    }, 10);
  }

  successModalVisibleChanged(visible: boolean) {
    if (this.successModalVisible && !visible) this.router.navigate(['/']);
  }

  goToStep(props: {
    step?:
      | 'register-email'
      | 'validate-code'
      | 'register-user'
      | 'register-company'
      | 'register-address';
    infoMessage?: string;
    errorMessage?: string;
  }) {
    if (props.step) this.activeStep = props.step;
    this.message = props.infoMessage;
    this.errorMessage = props.errorMessage;
  }
}
